import { Button, Tooltip } from "antd";
import { CheckCircle, X } from "@phosphor-icons/react";
import type { GridOperationContextAction } from "./GridOperationMenu.tsx";
import { getLocale, t } from "../../../features/store-language/i18n.ts";
import "../../../features/platform-admin/platform-admin-tokens.css";
import "./GridRowActions.css";

export const isOperationColumn = (column: { field?: string; colId?: string }) =>
  ["operation", "operations", "action", "actions"].includes(column.colId || column.field || "");

/** Same resolver/executor as the toolbar, with direct touch controls per row. */
export function GridInlineActions({ actions, disabled, pending, onRun }: {
  actions: GridOperationContextAction[];
  disabled: boolean;
  pending?: string;
  onRun: (action: GridOperationContextAction, event: React.MouseEvent) => void;
}) {
  return (
    <div className="ag-grid-inline-actions">
      {actions.map(function (action) {
        return (
          <Tooltip
            key={action.key}
            title={action.tooltip || action.label}
          >
            <Button
              type="text"
              danger={action.danger}
              icon={action.icon}
              disabled={disabled || action.disabled || Boolean(pending)}
              loading={pending === action.key}
              onClick={function (event) {
                event.stopPropagation();
                onRun(action, event);
              }}
            >
              <span className="platform-admin__sr-only">{action.label}</span>
            </Button>
          </Tooltip>
        );
      })}
    </div>
  );
}

/** Same compact utility strip as Raj EnterpriseGrid; page commands remain above it. */
export default function GridRowActions({ actions, rowNumber, selectedCount, disabled, pending, onRun, onClear, hideEmptyHint = false }: {
  actions: GridOperationContextAction[];
  rowNumber?: number;
  selectedCount: number;
  disabled: boolean;
  pending?: string;
  hideEmptyHint?: boolean;
  onRun: (action: GridOperationContextAction, event: React.MouseEvent) => void;
  onClear: () => void;
}) {
  const active = rowNumber !== undefined || selectedCount > 0;
  return (
    <div className="enterprise-grid__selection-toolbar-slot">
      <div
        aria-label={t("عملیات ردیف‌ها")}
        className={`enterprise-grid__selection-toolbar ${active ? "is-active" : "is-idle"}`}
        role="toolbar"
      >
        {active ? (
          <div
            aria-live="polite"
            className="enterprise-grid__selection-summary"
          >
            <CheckCircle size={19} />
            <span>
              {selectedCount > 0
                ? t("{{value0}} ردیف انتخاب شده", { value0: selectedCount.toLocaleString(getLocale()) })
                : `${t("ردیف")} ${rowNumber?.toLocaleString(getLocale())}`}
            </span>
          </div>
        ) : null}
        <div
          aria-disabled={disabled}
          className="enterprise-grid__selection-actions"
        >
          {actions.map(function (action) {
            return (
              <Tooltip
                key={action.key}
                title={action.tooltip}
              >
                <Button
                  size="small"
                  danger={action.danger}
                  icon={action.icon}
                  disabled={disabled || action.disabled || Boolean(pending)}
                  loading={pending === action.key}
                  onClick={function (event) { onRun(action, event); }}
                >
                  {action.label}
                </Button>
              </Tooltip>
            );
          })}
          {!actions.length && !hideEmptyHint ? <span>{t("برای انجام عملیات، یک ردیف را انتخاب کنید.")}</span> : null}
        </div>
        {active ? (
          <Button
            className="enterprise-grid__clear-selection"
            aria-label={t("لغو انتخاب")}
            type="text"
            icon={<X size={17} />}
            disabled={Boolean(pending)}
            onClick={onClear}
          >
            {t("لغو انتخاب")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
